import { memo, useCallback, useState, type ChangeEvent, type FC } from "react";
import { Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import StyledTextArea from "src/components/reusable/StyledTextArea";
import { WHATSAPP_EDITOR_KEY } from "../WhatsAppCampaignConstants";

const AI_KEY = `${WHATSAPP_EDITOR_KEY}.form.ai`;

interface AiPromptBoxProps {
  isGenerating: boolean;
  onGenerate: (_prompt: string) => void;
}

const AiPromptBox: FC<AiPromptBoxProps> = ({ isGenerating, onGenerate }) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState("");
  const canSubmit = Boolean(prompt.trim()) && !isGenerating;

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => setPrompt(event.target.value),
    []
  );
  const handleSubmit = useCallback(() => onGenerate(prompt), [onGenerate, prompt]);

  return (
    <section className="wa-ai-prompt" aria-labelledby="wa-ai-prompt-title">
      <header className="wa-ai-prompt__header">
        <Sparkles size={16} aria-hidden />
        <h3 id="wa-ai-prompt-title" className="wa-ai-prompt__title">
          {t(`${AI_KEY}.title`)}
        </h3>
        <span className="wa-ai-prompt__hint">{t(`${AI_KEY}.hint`)}</span>
      </header>
      <div className="wa-ai-prompt__field">
        <StyledTextArea
          value={prompt}
          onChange={handleChange}
          autoSize={{ minRows: 3, maxRows: 8 }}
          maxLength={1000}
          placeholder={t(`${AI_KEY}.placeholder`)}
          aria-label={t(`${AI_KEY}.title`)}
          disabled={isGenerating}
          className="campaign-flow__input wa-ai-prompt__input"
        />
        <button
          type="button"
          className="button button--primary campaign-flow__action wa-ai-prompt__submit"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {t(isGenerating ? `${AI_KEY}.generating` : `${AI_KEY}.done`)}
        </button>
      </div>
    </section>
  );
};

export default memo(AiPromptBox);
